
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { getValuesByKeys } from 'utils'

import { alpha, Divider, List, ListItem, ListItemButton, ListItemIcon, Stack, Typography, useTheme } from '@mui/material'

import { Button, CheckBox, hideScrollbar } from '@repo/ui'
import { CertificatePreview,CompanyPreview, OrderPreview, OwnerPreview, PaymentCell, Preview, ProductPreview, Search, AddressCell, UserBlock } from 'components'


interface SearchSelectProps {
  items: any[]
  source? : string
  onSearchClick?: () => void
  button?: React.ReactNode
  searchParams? : {
    fields?: string[],
    preview?: string | null,
    reference?: string
  }
  searchButton?: React.ReactNode
  singleSelect?: boolean
  selected?: any[]
  showClear?: boolean
  showSelect?: boolean
  /** Force the search input to render even in singleSelect mode (it's otherwise
   *  only shown for multi-select). */
  showSearch?: boolean
  /** Suppress the component's OWN search input entirely — used when an outer field
   *  owns the search box and drives filtering via `externalSearch` (VERA-455 E: the
   *  filter drawer types in the field, the dropdown shows options with no 2nd search). */
  hideSearch?: boolean
  /** Controlled search term supplied from outside. When defined, it drives filtering
   *  instead of the internal search state (pairs with `hideSearch`). */
  externalSearch?: string
  listItemSecondaryAction?: any
  /** Extra row appended inside the results `List` (see the render site for why). */
  listFooter?: React.ReactNode
  // Opt-in brand "purple gradient" hover on list items (used by the table Filter
  // dropdown). Default off so other usages (Add Owner / Add modals) keep the
  // neutral grey hover.
  hoverGradient?: boolean
  onClose?: () => void
  onClear?: () => void
  onChange?: (items: any[]) => void
  onApply?: (items: any[]) => void
  /**
   * THE SELECTION IS THE COMMIT — report every tick through `onChange` and render no Apply.
   *
   * `source: 'filters'` alone cannot decide this: the same `source` is used by the filter
   * drawer's fields AND by the modal pickers built on `SearchFilter` (`notFilter`), and only
   * the former applies as you go. So the caller states it.
   *
   * A dropdown that auto-applies must not offer an Apply — a button that does what has
   * already happened teaches the operator that their clicks did NOT take effect until they
   * pressed it. Clear stays: it is an action, not a commit.
   */
  autoApply?: boolean
}

// Match two records for selection. Prefer a stable `id` (object instances differ
// across re-fetches / reference extraction, so raw identity is unreliable); fall
// back to identity for primitive/id-less items (e.g. the no-preview Preview case).
const sameItem = (a: any, b: any) =>
  a?.id != null && b?.id != null ? a.id === b.id : a === b

export const SearchSelect = forwardRef<unknown, SearchSelectProps>((props, ref) => {
  const { 
    source, 
    items = [], 
    onSearchClick,
    button,
    searchParams, 
    searchButton, 
    selected = [],
    singleSelect = false,
    showClear = true,
    showSelect = true,
    showSearch = false,
    hideSearch = false,
    externalSearch,
    listItemSecondaryAction,
    listFooter,
    hoverGradient = false,
    autoApply = false,
    onClear = () => undefined,
    onClose = () => undefined, 
    onChange = () => undefined, 
    onApply = () => undefined
  } = props
  const theme = useTheme()
  const elementRef = useRef(null);
  const [search, setSearch] = useState('')
  const [selectedItems, setSelectedItems] = useState(selected)
  const [selectedItemWidth, setSelectedItemWidth] = useState(0);
  // 'all' shows every (search-filtered) row; 'selected' narrows to just the checked
  // items — a scalable review affordance for long lists, replacing the old chip strip.
  const [viewMode, setViewMode] = useState<'all' | 'selected'>('all')

  // Clear the ENTIRE selection (not just visible rows) and propagate to the parent.
  const handleClearAll = () => {
    setSelectedItems([])
    if (source != 'filters' || autoApply) {
      onChange?.([])
    }
    setViewMode('all')
  }

  //TODO: use ref to set width of the selectedItems list
  useEffect(() => {
    if (elementRef.current) {
      const width = elementRef.current.getBoundingClientRect().width;
      setSelectedItemWidth((width / 2)  - 4)
    }
  }, []);

  // The `if (onSearchClick) { useEffect(...) }` wrapper this replaces was a
  // hooks-order violation: a render where `onSearchClick` was absent ran one fewer
  // hook. The condition moved inside the effect — same behaviour, same `[selected]`
  // dep, no conditional hook.
  useEffect(() => {
    if (!onSearchClick) return
    setSelectedItems(selected)
  }, [selected])

  /**
   * Re-seed the checked set when the CONTENT of `selected` changes.
   *
   * `selectedItems` is seeded from `selected` at mount, which was enough while the only way to
   * check something was to click it. A scan can now check an option from outside this component
   * while the dropdown is open, and without this the checkbox would not move — the value would be
   * in the filter and the list would say otherwise.
   *
   * Keyed on the ids, not the array identity: the parent recomputes `selected` on every render,
   * and re-seeding on identity would wipe an in-progress selection on the next keystroke.
   */
  const selectedKey = (selected ?? []).map((item: any) => item?.id ?? item).join('|')
  useEffect(() => {
    setSelectedItems(selected ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey])

  // A parent field can own the search box and drive filtering via `externalSearch`
  // (VERA-455 E). When it's defined, it wins over the internal `search` state.
  const activeSearch = externalSearch !== undefined ? externalSearch : search

  /**
   * Dereference `searchParams.reference` ONCE, up front, rather than in the render map.
   *
   * The option a user actually picks is the referenced object (a certificate row's `.customer`
   * for the Owner filter, its `.product` for Product) — that is what the row click selects and
   * what the caller reads back. Doing the dereference only at render time meant everything else
   * in this component still operated on the OUTER row: Select-all pushed certificate rows into a
   * selection of owners, and the search matched `searchParams.fields` (`first_name`, `title`)
   * against a row that has no such keys, so typing filtered everything away. One dereference,
   * applied everywhere, keeps selection, search and rendering talking about the same object.
   */
  const referenceKey = searchParams?.reference
  const resolvedItems = referenceKey
    ? items.map((item: any) => item?.[referenceKey]).filter((item: any) => item != null)
    : items

  // Filter items based on search and only search the values of the specificed fields from each item
  const searchItems = (activeSearch.trim() === '' || !searchParams?.fields?.length)
    ? resolvedItems
    : resolvedItems.filter(item => {
        try {
          const values = getValuesByKeys(item, searchParams.fields)
          const searchableText = JSON.stringify(values).toLowerCase()
          return searchableText.includes(activeSearch.toLowerCase())
        } catch (error) {
          console.error('Error filtering item:', error, item)
          return false
        }
      })

  // The rows actually rendered: 'selected' view narrows the search-filtered set to
  // just the checked items (composes with search). Falls back to 'all' when nothing is
  // selected (the toggle is hidden then), so you can't get stranded on an empty list.
  const effectiveViewMode = selectedItems.length > 0 ? viewMode : 'all'
  const displayItems = effectiveViewMode === 'selected'
    ? searchItems.filter(item => selectedItems.some((s) => sameItem(s, item)))
    : searchItems

  /**
   * Nothing to show at all — no rows AND no caller-supplied footer row.
   *
   * Read the long note at the render site for why the footer is part of the test
   * rather than just the row count. Hoisted here because the list's own padding is
   * collapsed for this branch too, and the two decisions have to agree: computing it
   * twice is how they would drift.
   */
  const isEmptyState = displayItems.length === 0 && !listFooter

  // handle selected items on checkbox toggle
  const handleSelect = (item) => () => {
    // Prevent selecting imported items
    if (item.imported === true) {
      return;
    }

    if (singleSelect) {
      onApply(item)
      handleClear()
    } else {
      const currentIndex = selectedItems.findIndex((s) => sameItem(s, item));
      const newSelectedItems = [...selectedItems];

      if (currentIndex === -1) {
        newSelectedItems.push(item);
      } else {
        newSelectedItems.splice(currentIndex, 1);
      }
      setSelectedItems(newSelectedItems);
      if (source != 'filters' || autoApply) {
        onChange?.(newSelectedItems)
      }
    }
  };

  // Handle select all / deselect all
  const handleSelectAll = (event?: React.ChangeEvent<HTMLInputElement>) => {
    // Only select items that are not already imported
    const selectableItems = searchItems.filter(item => item.imported !== true);
    const allSelected = selectableItems.every(item => selectedItems.some((s) => sameItem(s, item)));

    if (allSelected) {
      // Deselect all - remove all searchable items from selection
      const newSelectedItems = selectedItems.filter(item => !selectableItems.some((s) => sameItem(s, item)));
      setSelectedItems(newSelectedItems);
      if (source != 'filters' || autoApply) {
        onChange?.(newSelectedItems);
      }
    } else {
      // Select all - add all selectable items
      const newSelectedItems = [...selectedItems];
      selectableItems.forEach(item => {
        if (!newSelectedItems.some((s) => sameItem(s, item))) {
          newSelectedItems.push(item);
        }
      });
      setSelectedItems(newSelectedItems);
      if (source != 'filters' || autoApply) {
        onChange?.(newSelectedItems);
      }
    }
  };

  const handleApply = () => {
    onApply?.(selectedItems)
    handleClear()
  }

  const handleClear = () => {
    setSelectedItems([])
    onClear()
  }

  useImperativeHandle(ref, () => ({
    handleClear: handleClear,
  }))

  // Check if all selectable items are selected
  const selectableItems = searchItems.filter(item => item.imported !== true);
  const allSelected = selectableItems.length > 0 && selectableItems.every(item => selectedItems.some((s) => sameItem(s, item)));
  const someSelected = selectableItems.some(item => selectedItems.some((s) => sameItem(s, item))) && !allSelected;

  return (
  <Stack ref={elementRef} sx={{ overflowX: 'hidden' }}>
    {(!singleSelect || showSearch) && !hideSearch && (
      <Stack spacing={2} direction="row" pb={3} justifyContent="space-between">
        {/* TODO: update search to use the button width? */}
        {button ? (
          <>{button}</>
        ) : (
          // flex:1 + minWidth:0 (not width:100%) so the search SHRINKS to share the row
          // with the searchButton (e.g. the integrations dropdown) instead of forcing it
          // to overflow / get clipped by the modal edge.
          <Stack onClick={onSearchClick} sx={{display: 'flex', flex: 1, minWidth: 0}}>
            <Search onChange={(value) => setSearch(value)} fullWidth={true}/>
          </Stack>
        )}
        {/* Only reserve the button slot when there's actually a button — otherwise the
            Stack spacing={2} left an 8px gap that shrank the search field below its
            wrapper width, so the search bar came out narrower than the dropdown popover
            (which matches the full wrapper width). Table filters keep their scan button. */}
        {searchButton && (
          <Stack pt={.5}>
            {searchButton}
          </Stack>
        )}
      </Stack>
    )}
    {/* The All / Selected(N) view filter + Clear all used to live in their OWN bar
        here, inserted only when something was selected — which grew the modal and, since
        it's vertically centered, made it jump up on first select. They now live on the
        RIGHT of the always-present Select-All header row below (secondaryAction), so
        selecting adds them inline on an existing row instead of adding a row. */}
    {showSelect && (
      <>
        {/* No standalone divider here — the Select-All row already has a bottom
            border; a second divider + the centering left a dark dead-gap above the
            list (point 3). */}
        <Stack
          // Match the data-table checkbox colours (Table.tsx): dimmed white when idle,
          // full white when checked, on dark; darker outline in light mode. The shared
          // @repo/ui CheckBox is a fixed grey[900] which reads wrong on the dark modal.
          sx={{
            '& .MuiCheckbox-root': {
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.55)
                : theme.palette.primary[300],
            },
            '& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate': {
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.primary[300],
            },
          }}
        >
            {/* Select All / Deselect All */}
            {!singleSelect && searchItems.length > 0 && (
              // Mirror the row structure exactly (ListItem > ListItemButton >
              // ListItemIcon pl:4) so the Select-All checkbox lines up with the row
              // checkboxes by construction, instead of matching guessed paddings.
              <ListItem
                disablePadding
                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                // View filter + Clear all sit here (right of Select All) so they appear on
                // an existing row when you select — no inserted bar, no modal jump. Only
                // shown once something is selected. stopPropagation keeps clicks off the
                // Select-All button underneath.
                secondaryAction={
                  selectedItems.length > 0 ? (
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Stack
                        direction="row"
                        sx={{ border: `1px solid ${theme.palette.primary[100]}`, borderRadius: 2, overflow: 'hidden' }}
                      >
                        {(['all', 'selected'] as const).map((mode) => (
                          <Stack
                            key={mode}
                            onClick={(e) => { e.stopPropagation(); setViewMode(mode) }}
                            sx={{
                              px: 2,
                              py: 0.5,
                              cursor: 'pointer',
                              userSelect: 'none',
                              ...(mode === 'selected' && { borderLeft: `1px solid ${theme.palette.primary[100]}` }),
                              backgroundColor: viewMode === mode ? theme.palette.action.selected : 'transparent',
                              '&:hover': { backgroundColor: viewMode === mode ? theme.palette.action.selected : theme.palette.action.hover },
                            }}
                          >
                            <Typography variant="body3" color={viewMode === mode ? 'text.primary' : 'text.secondary'}>
                              {mode === 'all' ? 'All' : `Selected ${selectedItems.length}`}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                      <Typography
                        variant="body3"
                        onClick={(e) => { e.stopPropagation(); handleClearAll() }}
                        // Neutral grey (not brand purple) so Select all/Clear all read the
                        // same everywhere — parity with the SelectField menu's grey Clear all.
                        sx={{
                          cursor: 'pointer',
                          color: 'text.secondary',
                          '&:hover': { textDecoration: 'underline', color: 'text.primary' },
                        }}
                      >
                        Clear all
                      </Typography>
                    </Stack>
                  ) : null
                }
              >
                <ListItemButton
                  dense
                  onClick={() => handleSelectAll()}
                  disabled={selectableItems.length === 0}
                  disableRipple
                  sx={{
                    borderRadius: 2,
                    minHeight: 40,
                    // Select All hover matches the item rows — brand tint when the list
                    // uses the brand hover (filter dropdown), transparent otherwise — so
                    // it never flashes the default grey (VERA-455).
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: hoverGradient ? 'rgba(127, 72, 223, 0.12)' : 'transparent',
                    },
                  }}
                >
                  <ListItemIcon sx={{ pl: 4, minWidth: 32, maxWidth: 32 }}>
                    <CheckBox
                      name="checkbox-select-all"
                      edge="start"
                      checked={allSelected}
                      indeterminate={someSelected}
                      disabled={selectableItems.length === 0}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <Typography
                    variant="body2"
                    color={selectableItems.length === 0 ? "text.disabled" : "text.secondary"}
                    // Left-align with the row IMAGES below (not the titles) — the image is
                    // the first content in each row, offset ~13px from the checkbox column.
                    // Tuned live against the ProductPreview image left edge.
                    sx={{ ml: '13px' }}
                  >
                    {selectableItems.length === 0
                      ? 'All items imported'
                      : allSelected
                        ? 'Deselect All'
                        : 'Select All'}
                  </Typography>
                </ListItemButton>
              </ListItem>
            )}
            <List
              sx={{
                width: '100%',
                maxHeight: `calc(40vh)`,
                // Gap below the Select-All divider so the first row isn't flush against
                // it (VERA-455); plain top padding when there's no Select-All row.
                //
                // Collapsed for the empty state. That padding exists to separate the
                // first RESULT from the divider above it, and there is no first result
                // here — combined with MUI List's own default 8px top/bottom it wrapped
                // a one-line message in ~64px of box, which read as a large empty panel
                // rather than a short answer.
                pt: isEmptyState ? 0 : 2,
                pb: isEmptyState ? 0 : undefined,
                overflowY: 'auto',
                overflowX: 'hidden',
                // Transparent so the list reads as the same surface as the modal/drawer
                // it sits in (background.paper here rendered as a lighter grey block in
                // dark mode against the container surface).
                bgcolor: 'transparent',
                ...hideScrollbar,
              }}
            >
              {/**
                * "No results" is suppressed whenever a `listFooter` is rendering.
                *
                * The two together are a contradiction the user can see: the transfer
                * picker showed "No results" AND, directly beneath it, an offer to send
                * to the address just typed. The list is not empty — it has one option,
                * which happens to be supplied by the caller rather than by the search.
                *
                * Note this is NOT the same condition as `listFooter` merely being
                * PASSED: the transfer modal's footer returns `null` for a query that is
                * not yet a valid email, and in that case the list really is empty and
                * must still say so. `listFooter` here is the already-evaluated node, so
                * a null footer leaves the empty state intact.
                */}
              {isEmptyState ? (
                /**
                 * The empty state occupies ONE ROW'S WORTH of the list, aligned with
                 * where a row's content starts — not a bare line jammed into the
                 * top-left corner.
                 *
                 * What it replaced and why each part was wrong (all measured, not
                 * eyeballed): `pl={0}` put the text at 0px from the list's left edge
                 * while every result row's content starts at 28px (the
                 * ListItemButton's 16px `pl:4` plus `Preview`'s 12px `px:3`), so the
                 * only thing the list ever renders flush to its own border was this.
                 * `alignItems="left"` is not a valid `align-items` value at all — the
                 * browser drops the declaration, which is why it never did anything.
                 * And `justifyContent="center"` on a column Stack centres along the
                 * MAIN axis, which in an auto-height box is a no-op: the block came
                 * out 34px tall with all 16px of its `pt` above the text and nothing
                 * below it, i.e. top-aligned, not centred.
                 *
                 * So: a real height to centre WITHIN, `justifyContent: center` now
                 * meaning something because the box has height, and the rows' 28px
                 * inset.
                 *
                 * The height is 36, not the rows' own 48. Matching a row read well as
                 * "occupies one row's worth", but a row is 48 because it carries an
                 * avatar and two lines; this carries one short line. At 48 — plus the
                 * list's 8px `pt` and MUI List's own 8px top/bottom — a one-line
                 * message sat in a ~64px panel, which reads as a broken empty state
                 * rather than a short answer. The list padding is collapsed for this
                 * branch too; see the `pt`/`pb` above.
                 */
                <Stack
                  justifyContent="center"
                  sx={{ minHeight: 36, pl: '28px' }}
                >
                  <Typography variant="body3" color="text.secondary">No results</Typography>
                </Stack>
              ) : (
                <>
                  {displayItems.map((item, index) => {
                    const labelId = `checkbox-list-label-${item?.id}`
                    // `reference` is dereferenced up front (see `resolvedItems`) so selection,
                    // search and rendering all operate on the same object.
                    if (item != null) {
                      return (
                          <ListItem
                            // Stable id key (not index) + no per-item Fade: the staggered
                            // Fade replayed on every keystroke as the filtered list
                            // re-rendered, causing the flash while typing in search.
                            key={item?.id ?? `item-${index}`}
                            disablePadding
                            onClick={handleSelect(item)}
                            secondaryAction={listItemSecondaryAction && listItemSecondaryAction(item)}
                          >
                            <ListItemButton
                              dense
                              disabled={item.imported === true}
                              sx={{
                                borderRadius: 2,
                                minHeight: 48,
                                // Reserve room on the right for the secondaryAction (e.g.
                                // the source badge) so the title truncates (noWrap) instead
                                // of running underneath it.
                                ...(listItemSecondaryAction && { pr: '104px' }),
                                opacity: item.imported === true ? 0.6 : 1,
                                cursor: item.imported === true ? 'not-allowed' : 'pointer',
                                ...(hoverGradient && {
                                  // Subtle brand-purple hover (12% brand tint, brand[600] #7F48DF),
                                  // matching the pre-VERA-816 hover instead of neutral grey.
                                  '&:hover, &.Mui-focusVisible': {
                                    backgroundColor: 'rgba(127, 72, 223, 0.12)',
                                  },
                                }),
                              }}
                            >
                              {!singleSelect && (
                                <ListItemIcon sx={{pl: 4, minWidth: 32, maxWidth: 32}}>
                                  <CheckBox
                                    name={`checkbox-${item.id}`}
                                    edge="start"
                                    checked={selectedItems.some((s) => sameItem(s, item))}
                                    disabled={item.imported === true}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': labelId }}
                                  />
                                </ListItemIcon>
                              )}
                              {/* TODO: llast thign to remove to make this a pure component */}
                              {searchParams.preview == 'certificates' && (<CertificatePreview size="medium" certificate={item} />)}
                              {searchParams.preview == 'owners' && (<OwnerPreview size="medium" owner={item} />)}
                              {searchParams.preview == 'products' && (<ProductPreview size="medium" product={item} />)}
                              {searchParams.preview == 'companies' && (<CompanyPreview size="medium" company={item} />)}
                              {searchParams.preview == 'orders' && (<OrderPreview size="medium" product={item} />)}
                              {searchParams.preview == 'payments' && (<PaymentCell paymentMethod={item} />)}
                              {searchParams.preview == 'addressess' && (<AddressCell address={item} />)}
                              {/*
                                COMPANY MEMBERS. `UserBlock` deliberately, not `OwnerPreview`:
                                an owner is a consumer record (`Customer`) and a company user is
                                a staff record, and `UserBlock` is already the row every people
                                picker in the app draws — `AssignUsersModal`, `UserChecklist`,
                                `BulkUserActionModal`, `DeleteCompanyUserModal`. Adding the case
                                here rather than hand-rolling a fifth people list is the §47
                                direction: improve the shared base so both the dedicated Assign
                                modal and a picker inside a form render the same person.
                              */}
                              {/*
                                `searchParams?.`, not `searchParams.` like its siblings.
                                `searchParams` has no default in the destructure, so every
                                unguarded line in this block is an existing TS18048 in the
                                platform's error baseline — nine of them. Adding a tenth to
                                match the local style would raise that baseline for a new
                                line, which is the wrong direction. The siblings are left
                                alone deliberately: fixing them is a behaviour question
                                (line 565 would go from throwing to reading `item[undefined]`
                                for a caller that omits `searchParams`), not a cleanup to
                                smuggle in here.
                              */}
                              {searchParams?.preview == 'users' && (<UserBlock user={item} />)}
                              {searchParams.preview == undefined && (<Preview size="medium" item={item[searchParams.fields[0]]}/>)}
                            </ListItemButton>
                          </ListItem>
                      );
                    }
                  })}
                </>
              )}
              {/**
                * A caller-supplied row at the BOTTOM of the list, inside the same
                * scroll container as the results.
                *
                * It exists because a picker's options are not always the whole answer:
                * the transfer modal's "Transfer to" has to offer an address that
                * matches nobody (the API provisions and invites that recipient), and
                * before this the list dead-ended on "No results". Rendered here rather
                * than below the List so it scrolls WITH the results instead of pinning
                * itself over them, and so it inherits the list's own left inset.
                *
                * Deliberately rendered in BOTH branches — an extra option is not an
                * empty state, and suppressing it when results exist would make it
                * unreachable exactly when someone is refining a search.
                */}
              {listFooter}
            </List>
        </Stack>
      </>
    )}
    {/* AUTO-APPLY MODE: the ticks already applied, so there is nothing here to commit — what was
        missing was a way OUT. This footer used to be a full-width "Clear", which made DESTROYING
        the selection the only labelled exit (the old comment said as much: "the ONLY escape from
        a selection that committed itself") and duplicated the "Clear all" sitting beside
        `Selected N` in the header. "Done" dismisses without claiming to apply something that
        already happened, and without colliding with the real Apply below, which still means
        commit in picker mode. Clearing now lives only in the header, at the one scope it can
        unambiguously mean: this field's selection. Shown even with nothing ticked — "how do I
        close this" doesn't depend on having picked anything. */}
    {autoApply && !singleSelect && (
      <Stack>
        <Divider/>
        <Stack direction="row" pt={3}>
          <Button
            fullWidth
            disableElevation
            variant="secondary"
            onClick={() => onClose()}
          >
            Done
          </Button>
        </Stack>
      </Stack>
    )}
    {/* PICKER MODE (`notFilter`): the picks are a submission, so there IS something to confirm
        and a reason to back out of it. Unchanged. */}
    {!autoApply && showClear && selectedItems.length > 0 && !singleSelect && (
      <Stack>
        <Divider/>
        <Stack direction="row" pt={3} spacing={3}>
          <Button
            fullWidth
            disableElevation
            variant="secondary"
            onClick={handleClear}
          >
            Clear
          </Button>
          {onApply && (
            <Button
              fullWidth
              disableElevation
              onClick={handleApply}
            >
              Apply
            </Button>
          )}
        </Stack>
      </Stack>
    )}
  </Stack>
  )
})
